import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { setTokens, clearTokens, getAccessToken, refreshSession } from "@/lib/api";
import { getUtm, clearUtm } from "@/lib/utm";

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const AuthContext = createContext(null);

function stripTokens(data) {
  // Login/register responses carry the user fields plus access/refresh tokens.
  const { access_token, refresh_token, ...user } = data || {};
  return user;
}

export function AuthProvider({ children }) {
  // null = checking, object = authed, false = not authed
  const [user, setUser] = useState(null);
  // Mirror of `user` so the (mount-once) expiry listener can tell whether there
  // was an active session worth notifying about.
  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const refresh = useCallback(async () => {
    // No stored token => no session to restore.
    if (!getAccessToken()) {
      setUser(false);
      return false;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (err) {
      // Access token expired? Mint a fresh one from the refresh token, then retry
      // /me once. Only fall back to "not authed" if the refresh itself fails.
      if (err?.response?.status === 401) {
        try {
          await refreshSession();
          const { data } = await api.get("/auth/me");
          setUser(data);
          return data;
        } catch {
          clearTokens();
          setUser(false);
          return false;
        }
      }
      setUser(false);
      return false;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // The api instance broadcasts this when a 401 cannot be recovered via refresh
  // (refresh token expired/invalid). Drop the session so ProtectedRoute redirects.
  useEffect(() => {
    const onExpired = () => {
      // Only surface the notice if there was actually an authenticated session.
      if (userRef.current) {
        toast.error("Session expired", {
          description: "Please sign in again to continue.",
        });
      }
      clearTokens();
      setUser(false);
    };
    window.addEventListener("sema:auth-expired", onExpired);
    return () => window.removeEventListener("sema:auth-expired", onExpired);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setTokens(data.access_token, data.refresh_token);
    const u = stripTokens(data);
    setUser(u);
    return u;
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", {
      name, email, password, utm: getUtm(), origin: window.location.origin,
    });
    clearUtm();
    // Double opt-in: when verification is required the API returns no tokens —
    // the account is inactive until the emailed link is clicked.
    if (data?.status === "verification_required") return data;
    // Verification disabled → immediate login (legacy path).
    setTokens(data.access_token, data.refresh_token);
    const u = stripTokens(data);
    setUser(u);
    return { ...u, status: "authenticated" };
  };

  const verifyEmail = async (token) => {
    const { data } = await api.post("/auth/verify-email", { token });
    setTokens(data.access_token, data.refresh_token);
    setUser(stripTokens(data));
    return data;
  };

  const resendVerification = async (email) => {
    const { data } = await api.post("/auth/resend-verification", {
      email, origin: window.location.origin,
    });
    return data;
  };

  const magicRequest = async (email) => {
    await api.post("/auth/magic/request", { email, origin: window.location.origin, utm: getUtm() });
  };

  const magicVerify = async (token) => {
    const { data } = await api.post("/auth/magic/verify", { token });
    setTokens(data.access_token, data.refresh_token);
    setUser(stripTokens(data));
    return data; // includes is_new
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout", {});
    } finally {
      clearTokens();
      setUser(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, refresh, magicRequest, magicVerify, verifyEmail, resendVerification }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useSession() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useSession must be used within AuthProvider");
  return ctx;
}
